import React, { useState, useEffect } from 'react';
import { User, Search, Download, RefreshCw, AlertTriangle, X, CheckCircle, Eye, Edit, Trash2, ArrowUp, ArrowDown } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  Menu,
  Button
} from '@mui/material';
import MainService from '../../services/MainService';
import * as XLSX from 'xlsx';
import { useNavigate } from 'react-router-dom';

const Customers = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSearchKeyword, setActiveSearchKeyword] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  useEffect(() => {
    loadCustomers(activeSearchKeyword);
  }, [currentPage, activeSearchKeyword, pageSize]);

  // Toast notification effects
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const loadCustomers = async (keyword = '') => {
    try {
      setLoading(true);
      setError('');
      
      // If "All" is selected, first get total count, then fetch all records
      if (pageSize === 'all') {
        // First request to get total count
        const countRequestData = {
          Keyword: keyword || '',
          Skip: 0,
          Take: 1
        };
        const countResponse = await MainService.CustomersInList(countRequestData);
        const total = countResponse?.Total || 0;
        
        if (total > 0) {
          // Now fetch all records
          const allRequestData = {
            Keyword: keyword || '',
            Skip: 0,
            Take: total
          };
          const allResponse = await MainService.CustomersInList(allRequestData);
          
          if (allResponse && allResponse.Data) {
            setCustomers(allResponse.Data || []);
            setTotalCount(total);
          } else {
            setCustomers([]);
            setTotalCount(0);
          }
        } else {
          setCustomers([]);
          setTotalCount(0);
        }
      } else {
        const skip = (currentPage - 1) * pageSize;
        const requestData = {
          Keyword: keyword || '',
          Skip: skip,
          Take: pageSize
        };

        const response = await MainService.CustomersInList(requestData);
        
        if (response && response.Data) {
          setCustomers(response.Data || []);
          setTotalCount(response.Total || 0);
        } else {
          setCustomers([]);
          setTotalCount(0);
        }
      }
    } catch (err) {
      console.error('Error loading customers:', err);
      setError('Failed to load customers');
      setCustomers([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    setActiveSearchKeyword(searchTerm);
  };

  const handleRefresh = async () => {
    setCurrentPage(1);
    setSearchTerm('');
    setActiveSearchKeyword('');
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const exportToExcel = () => {
    if (customers.length === 0) {
      setError('No customers to export');
      return;
    }

    try {
      const exportData = customers.map((customer, index) => ({
        '#': pageSize === 'all' ? index + 1 : (currentPage - 1) * pageSize + index + 1,
        'Name': customer.Name || 'N/A',
        'Mobile No1': customer.MobileNo1 || 'N/A',
        'Mobile No2': customer.MobileNo2 || 'N/A',
        'Tel No': customer.TelNo || 'N/A',
        'Email': customer.EmailId || 'N/A',
        'Location': customer.LocationName || 'N/A',
        'Emirate': customer.EmirateName || 'N/A',
        'Area': customer.AreaName || 'N/A',
        'Address': customer.Address || 'N/A',
        'PO Box': customer.POBOX || 'N/A',
        'Date': customer.Date ? new Date(customer.Date).toLocaleDateString('en-GB') : 'N/A'
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Customers');
      const fileName = `customers_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      setSuccess('Excel file exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      setError('Failed to export Excel file');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const handleContextMenu = (event, customer) => {
    event.preventDefault();
    setContextMenu(
      contextMenu === null
        ? {
            mouseX: event.clientX + 2,
            mouseY: event.clientY - 6,
          }
        : null,
    );
    setSelectedCustomer(customer);
  };

  const handleMenuClose = () => {
    setContextMenu(null);
    setSelectedCustomer(null);
  };

  const handleView = (customer) => {
    handleMenuClose();
    // TODO: Implement view functionality
    console.log('View customer:', customer);
  };

  const handleEdit = (customer) => {
    handleMenuClose();
    navigate(`/home/accounts/addCustomer/${customer.Id}`);
  };

  const handleDelete = (customer) => {
    handleMenuClose();
    // TODO: Implement delete functionality
    console.log('Delete customer:', customer);
  };

  const sortCustomers = (data) => {
    if (!sortField) return data;

    return [...data].sort((a, b) => {
      let aValue, bValue;

      switch (sortField) {
        case 'Name':
          aValue = a.Name || '';
          bValue = b.Name || '';
          break;
        case 'MobileNo1':
          aValue = a.MobileNo1 || '';
          bValue = b.MobileNo1 || '';
          break;
        case 'EmailId':
          aValue = a.EmailId || '';
          bValue = b.EmailId || '';
          break;
        case 'Location':
          aValue = a.LocationName || '';
          bValue = b.LocationName || '';
          break;
        default:
          return 0;
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const sortedCustomers = sortCustomers(customers);

  const totalPages = pageSize === 'all' ? 1 : Math.ceil(totalCount / pageSize);

  const SortIcon = ({ field }) => {
    if (sortField !== field) {
      return <ArrowUp className="w-3 h-3 opacity-30" />;
    }
    return sortOrder === 'asc' 
      ? <ArrowUp className="w-3 h-3" style={{ color: '#46955A' }} />
      : <ArrowDown className="w-3 h-3" style={{ color: '#46955A' }} />;
  };

  return (
    <div className="space-y-6">
      {/* Toast Messages */}
      {error && (
        <div className="fixed top-4 right-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="fixed top-4 right-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{success}</span>
          <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Table Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center mb-2">
              <User className="w-6 h-6 text-primary mr-2" />
              <h1 className="text-3xl font-bold text-gray-900">Customers</h1>
            </div>
            <Button
              variant="contained"
              startIcon={<User className="w-4 h-4" />}
              onClick={() => navigate('/home/accounts/addCustomer')}
              sx={{ backgroundColor: '#46955A', '&:hover': { backgroundColor: '#3d7d4f' } }}
            >
              Add Customer
            </Button>
          </div>

          {/* Search and Actions */}
          <div className="flex flex-col lg:flex-row gap-4 items-end">
            <div className="flex-1">
              <TextField
                fullWidth
                placeholder="Search by name or mobile number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch();
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search className="w-5 h-5 text-gray-400" />
                    </InputAdornment>
                  ),
                  sx: {
                    '& .MuiOutlinedInput-notchedOutline': {
                      border: 'none',
                      borderBottom: '1px solid #E5E7EB'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderBottom: '2px solid #46955A'
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderBottom: '2px solid #46955A'
                    }
                  }
                }}
                variant="outlined"
              />
            </div>
            <div className="flex items-center gap-2">
              <Tooltip title="Refresh">
                <IconButton
                  onClick={handleRefresh}
                  disabled={loading}
                  sx={{
                    color: '#46955A',
                    '&:hover': { backgroundColor: '#f0f9f4' },
                    '&:disabled': { color: '#9ca3af' }
                  }}
                >
                  <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Export to Excel">
                <IconButton
                  onClick={exportToExcel}
                  disabled={customers.length === 0}
                  sx={{
                    color: '#46955A',
                    '&:hover': { backgroundColor: '#f0f9f4' },
                    '&:disabled': { color: '#9ca3af' }
                  }}
                >
                  <Download className="w-5 h-5" />
                </IconButton>
              </Tooltip>
            </div>
          </div>
        </div>

        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            height: '500px',
            position: 'relative',
            border: '1px solid #E5E7EB',
            borderRadius: '0.5rem',
            '& .MuiPaper-root': {
              boxShadow: 'none'
            }
          }}
        >
          {loading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.75)',
                backdropFilter: 'blur(4px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 20
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <CircularProgress size={32} sx={{ color: '#46955A' }} />
                <span style={{ fontSize: '0.875rem', color: '#6B7280' }}>Loading...</span>
              </Box>
            </Box>
          )}
          <Table stickyHeader sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell align="center" sx={{ fontWeight: 600, fontSize: '0.75rem', backgroundColor: '#F9FAFB', width: '5%', padding: '8px' }}>#</TableCell>
                <TableCell 
                  align="left" 
                  sx={{ fontWeight: 600, fontSize: '0.75rem', backgroundColor: '#F9FAFB', width: '15%', cursor: 'pointer', padding: '8px', '&:hover': { backgroundColor: '#F3F4F6' } }}
                  onClick={() => handleSort('Name')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    Name
                    <SortIcon field="Name" />
                  </Box>
                </TableCell>
                <TableCell 
                  align="left" 
                  sx={{ fontWeight: 600, fontSize: '0.75rem', backgroundColor: '#F9FAFB', width: '12%', cursor: 'pointer', padding: '8px', '&:hover': { backgroundColor: '#F3F4F6' } }}
                  onClick={() => handleSort('MobileNo1')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    Mobile No1
                    <SortIcon field="MobileNo1" />
                  </Box>
                </TableCell>
                <TableCell align="left" sx={{ fontWeight: 600, fontSize: '0.75rem', backgroundColor: '#F9FAFB', width: '12%', padding: '8px' }}>Mobile No2</TableCell>
                <TableCell align="left" sx={{ fontWeight: 600, fontSize: '0.75rem', backgroundColor: '#F9FAFB', width: '12%', padding: '8px' }}>Tel No</TableCell>
                <TableCell 
                  align="left" 
                  sx={{ fontWeight: 600, fontSize: '0.75rem', backgroundColor: '#F9FAFB', width: '15%', cursor: 'pointer', padding: '8px', '&:hover': { backgroundColor: '#F3F4F6' } }}
                  onClick={() => handleSort('EmailId')}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    Email
                    <SortIcon field="EmailId" />
                  </Box>
                </TableCell>
                <TableCell align="left" sx={{ fontWeight: 600, fontSize: '0.75rem', backgroundColor: '#F9FAFB', width: '12%', padding: '8px' }}>Location</TableCell>
                <TableCell align="left" sx={{ fontWeight: 600, fontSize: '0.75rem', backgroundColor: '#F9FAFB', width: '10%', padding: '8px' }}>Emirate</TableCell>
                <TableCell align="left" sx={{ fontWeight: 600, fontSize: '0.75rem', backgroundColor: '#F9FAFB', width: '10%', padding: '8px' }}>Area</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedCustomers.length > 0 ? (
                sortedCustomers.map((customer, index) => (
                  <TableRow
                    key={customer.Id || index}
                    hover
                    onContextMenu={(e) => handleContextMenu(e, customer)}
                    sx={{
                      '&:hover': { backgroundColor: '#F9FAFB' },
                      '&:last-child td, &:last-child th': { border: 0 },
                      cursor: 'context-menu'
                    }}
                  >
                    <TableCell align="center" sx={{ fontSize: '0.875rem', color: '#6B7280', padding: '8px' }}>
                      {pageSize === 'all' 
                        ? index + 1 
                        : (currentPage - 1) * pageSize + index + 1}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem', fontWeight: 500, color: '#111827', padding: '8px' }}>
                      {customer.Name || 'N/A'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem', color: '#374151', padding: '8px' }}>
                      {customer.MobileNo1 || 'N/A'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem', color: '#374151', padding: '8px' }}>
                      {customer.MobileNo2 || 'N/A'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem', color: '#374151', padding: '8px' }}>
                      {customer.TelNo || 'N/A'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem', color: '#374151', padding: '8px' }}>
                      {customer.EmailId || 'N/A'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem', color: '#374151', padding: '8px' }}>
                      {customer.LocationName || 'N/A'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem', color: '#374151', padding: '8px' }}>
                      {customer.EmirateName || 'N/A'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem', color: '#374151', padding: '8px' }}>
                      {customer.AreaName || 'N/A'}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                      <User className="w-8 h-8 text-gray-400" />
                      <Box sx={{ color: '#6B7280' }}>
                        {searchTerm ? `No customers found for "${searchTerm}"` : 'No customers available'}
                      </Box>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Context Menu */}
        <Menu
          open={contextMenu !== null}
          onClose={handleMenuClose}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu !== null
              ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
              : undefined
          }
          PaperProps={{
            sx: {
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
              borderRadius: '0.5rem',
              minWidth: 150,
              mt: 0.5
            }
          }}
        >
          <MenuItem
            onClick={() => handleView(selectedCustomer)}
            sx={{
              fontSize: '0.875rem',
              py: 1.5,
              '&:hover': { backgroundColor: '#f0f9f4' }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Eye className="w-4 h-4" style={{ color: '#46955A' }} />
              <span>View</span>
            </Box>
          </MenuItem>
          <MenuItem
            onClick={() => handleEdit(selectedCustomer)}
            sx={{
              fontSize: '0.875rem',
              py: 1.5,
              '&:hover': { backgroundColor: '#f0f9f4' }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Edit className="w-4 h-4" style={{ color: '#46955A' }} />
              <span>Edit</span>
            </Box>
          </MenuItem>
          <MenuItem
            onClick={() => handleDelete(selectedCustomer)}
            sx={{
              fontSize: '0.875rem',
              py: 1.5,
              color: '#DC2626',
              '&:hover': { backgroundColor: '#FEE2E2' }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Trash2 className="w-4 h-4" />
              <span>Delete</span>
            </Box>
          </MenuItem>
        </Menu>

        {/* Footer with Pagination and Row Selection */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between', 
          alignItems: 'center',
          p: 2, 
          borderTop: '1px solid #E5E7EB',
          gap: 2
        }}>
          {/* Count Display */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2,
            flexWrap: 'wrap'
          }}>
            <span style={{ fontSize: '0.875rem', color: '#6B7280' }}>
              {totalCount > 0 
                ? pageSize === 'all'
                  ? `1-${totalCount} out of ${totalCount}`
                  : `${(currentPage - 1) * pageSize + 1}-${Math.min(currentPage * pageSize, totalCount)} out of ${totalCount}`
                : '0 out of 0'
              }
            </span>
            
            {/* Rows per page selection */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <span style={{ fontSize: '0.875rem', color: '#6B7280' }}>Rows per page:</span>
              <FormControl size="small" sx={{ minWidth: 80 }}>
                <Select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(e.target.value);
                    setCurrentPage(1);
                  }}
                  sx={{
                    fontSize: '0.875rem',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#E5E7EB'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#46955A'
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#46955A'
                    }
                  }}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={20}>20</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                  <MenuItem value="all">All</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          {/* Pagination */}
          {totalPages > 1 && (
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              sx={{
                '& .MuiPaginationItem-root': {
                  color: '#6B7280'
                },
                '& .Mui-selected': {
                  backgroundColor: '#46955A !important',
                  color: '#fff !important',
                  '&:hover': {
                    backgroundColor: '#3d7d4f !important'
                  }
                },
                '& .MuiPaginationItem-root:hover': {
                  backgroundColor: '#f0f9f4'
                }
              }}
            />
          )}
        </Box>
      </div>
    </div>
  );
};

export default Customers;

